<template>
  <div class="h-full">
    <div class="h-full flex flex-col">
      <div class="flex w-full pb-3 items-center flex-shrink-0 sm:pr-10">
        <div class="flex-grow flex items-center">
          <div class="text-13 font-600">{{ $t('projects.personas.title') }}</div>
        </div>
        <div class="ml-auto flex flex-row justify-center">
          <PillButton :text="$t('add_new')" primary @click="onCreateClicked" />
        </div>
      </div>
      <div class="w-full flex-grow overflow-hidden pb-2 flex flex-col">
        <template v-if="personas.length">
          <TableElevated :items="personas" :headers="personaHeaders" class="text-13 pr-3 sm:pr-10 min-h-300">
            <template v-slot:[`item.name`]="{ value }">
              <span class="text-13 font-500 truncate">{{ value }}</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <IconButton icon="edit" class="mr-1 text-primary" @click="onEditClicked(item)" />
              <IconButton class="text-primary" icon="delete" @click="onDeleteClicked(item)" />
            </template>
          </TableElevated>
        </template>
        <div v-else class="flex flex-col flex-grow items-center justify-center">
          <div class="my-2 text-gray-600">{{ $t('projects.personas.create_first_persona') }}</div>
          <PillButton class="my-3" :text="$t('add_new')" primary @click="onCreateClicked" />
        </div>
        <div class="flex-shrink-0 py-4 px-2 mt-2 border-t border-gray-300">
          <div class="mb-2 text-12 text-dark-text">Use a template</div>
          <div class="flex pb-3 flex-wrap -ml-3">
            <div v-for="(template, index) in predefinedTemplates" :key="index" class="w-1/2 md:w-1/3 lg:w-1/4 xl:w-1/5 pl-2 pt-3 pr-1">
              <div
                class="
                  select-none
                  cursor-pointer
                  shadow-mini-card-alt
                  rounded-14
                  flex flex-col
                  p-4
                  gap-2
                  h-full
                  justify-start
                  text-12
                  rounded-br-none
                  border border-solid border-secondary border-opacity-10
                  text-semi-dark-text text-opacity-80
                  flex-shrink-0
                "
              >
                <div class="flex items-center text-primary">
                  <Icon :name="template.icon" class="w-5 h-5 mr-2 flex-shrink-0" />
                  <span class="font-500">{{ template.label }}</span>
                </div>
                <div class="text-10">{{ template.description }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Modal v-if="fetching">
      <div class="flex flex-col font-700 items-center justify-center px-20 my-10">
        <div>{{ $t('loading_data') }}</div>
        <Icon name="loading_dots" class="w-14 mt-3 text-primary" />
      </div>
    </Modal>
  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import DeleteModal from '../../modals/DeleteModal.vue';
import CreatePersonaModal from '../../project/CreatePersonaModal.vue';

export default {
  name: 'Personas',
  data() {
    return {
      fetching: false,
      predefinedTemplates: [
        { label: 'Helpful Assistant', description: 'General-purpose AI for everyday tasks and customer support.', icon: 'support_agent' },
        { label: 'Analytical Advisor', description: 'Data-driven assistant for insights and decision support.', icon: 'insights' },
        { label: 'Friendly Coach', description: 'Motivational guide for productivity, learning, or personal development.', icon: 'self_improvement' },
        { label: 'Creative Brainstormer', description: 'Ideation and brainstorming partner for creative tasks.', icon: 'lightbulb' },
        { label: 'Process Guide', description: 'Step-by-step instructional assistant.', icon: 'menu_book' },
        { label: 'Conversational Concierge', description: 'High-touch, personalized service and recommendation assistant.', icon: 'room_service' },
        { label: 'Knowledge Expert', description: 'Deep-domain knowledge provider for any industry.', icon: 'school' },
        { label: 'Compliance Guardian', description: 'Ensuring rules and best practices are followed.', icon: 'verified_user' },
        { label: 'Curious Researcher', description: 'Research assistant for finding, summarizing, and comparing information.', icon: 'travel_explore' },
        { label: 'Empathetic Listener', description: 'Emotionally supportive conversational agent.', icon: 'volunteer_activism' },
      ],
    };
  },
  computed: {
    ...mapState(['projectData']),
    personas() {
      return Object.values(this.projectData[this.projectId]?.personas || {});
    },
    projectId() {
      return this.$route.params.projectId;
    },
    personaHeaders() {
      return [
        { key: 'name', title: this.$t('name') },
        { key: 'actions', title: this.$t('actions'), width: '50px' },
      ];
    },
  },
  methods: {
    ...mapActions(['showToastMessage', 'fetchProjectPersonas', 'deleteProjectPersona']),
    onCreateClicked() {
      this.$showModal(CreatePersonaModal, { projectId: this.projectId });
    },
    onEditClicked(persona) {
      this.$showModal(CreatePersonaModal, { projectId: this.projectId, editData: persona });
    },
    onDeleteClicked(persona) {
      const modal = this.$showModal(DeleteModal, {
        subtitle: this.$t('projects.personas.remove_persona', { templateName: persona.name }),
        info: null,
        onConfirm: async () => {
          await this.onDeleteConfirmClicked(persona, modal);
        },
      });
    },
    async onDeleteConfirmClicked(item) {
      await this.deleteProjectPersona({ project_id: this.projectId, persona_id: item.persona_id });
    },
  },
  watch: {
    projectId: {
      async handler() {
        this.fetching = true;
        await this.fetchProjectPersonas({ projectId: this.$route.params.projectId });
        this.fetching = false;
      },
      immediate: true,
    },
  },
};
</script>
